import { toJalaali, toGregorian, jalaaliMonthLength, isValidJalaaliDate } from 'jalaali-js';
import { toEnglishDigits, toPersianDigits } from './numbers';

export const MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

/** Saturday-first, as in the Iranian calendar. */
export const WEEKDAY_SHORT = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'] as const;
/** Indexed by `Date#getDay()` (0 = Sunday). */
const WEEKDAY_NAMES = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];

export interface JalaliDate {
  year: number;
  month: number; // 1-12
  day: number;
}

export const jalaliFromDate = (d: Date): JalaliDate => {
  const j = toJalaali(d);
  return { year: j.jy, month: j.jm, day: j.jd };
};

export const todayJalali = (): JalaliDate => jalaliFromDate(new Date());

export const jalaliToDate = (j: JalaliDate): Date => {
  const g = toGregorian(j.year, j.month, j.day);
  return new Date(g.gy, g.gm - 1, g.gd);
};

/** `YYYY-MM-DD` Gregorian — what the search endpoints expect. */
export const jalaliToApiDate = (j: JalaliDate): string => {
  const g = toGregorian(j.year, j.month, j.day);
  return `${g.gy}-${String(g.gm).padStart(2, '0')}-${String(g.gd).padStart(2, '0')}`;
};

export const apiDateToJalali = (iso: string): JalaliDate | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return jalaliFromDate(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};

export const addDays = (j: JalaliDate, days: number): JalaliDate => {
  const d = jalaliToDate(j);
  d.setDate(d.getDate() + days);
  return jalaliFromDate(d);
};

export const compareJalali = (a: JalaliDate, b: JalaliDate): number =>
  a.year - b.year || a.month - b.month || a.day - b.day;

export const monthLength = (year: number, month: number) => jalaaliMonthLength(year, month);

/** Index in the Saturday-first week (0 = Saturday). */
export const weekdayIndex = (j: JalaliDate): number => (jalaliToDate(j).getDay() + 1) % 7;

export const weekdayName = (j: JalaliDate): string =>
  WEEKDAY_NAMES[jalaliToDate(j).getDay()] ?? '';

export const formatJalaliShort = (j: JalaliDate) =>
  `${toPersianDigits(j.day)} ${MONTH_NAMES[j.month - 1]}`;
export const formatJalaliWithWeekday = (j: JalaliDate) =>
  `${weekdayName(j)} ${formatJalaliShort(j)}`;
export const formatJalaliFull = (j: JalaliDate) =>
  `${formatJalaliShort(j)} ${toPersianDigits(j.year)}`;

/** `1380/01/05` (ASCII digits). */
export const formatJalaliSlash = (j: JalaliDate) =>
  `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`;

export function parseJalaliString(text: string): JalaliDate | null {
  const m = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(toEnglishDigits(text.trim()));
  if (!m) return null;
  const j = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  return isValidJalaaliDate(j.year, j.month, j.day) ? j : null;
}

/**
 * Birth dates go to the API as ASCII `YYYY-MM-DD` Jalali strings, for intercity
 * tickets, door-to-door passengers and the profile alike (the Flutter app builds
 * them with `dateFormatter(Jalali(...))`).
 */
export const formatBirthDateForApi = (j: JalaliDate): string =>
  `${j.year}-${String(j.month).padStart(2, '0')}-${String(j.day).padStart(2, '0')}`;

/** ISO datetime → "1405-05-29  16:15" (Jalali, local time). */
export function formatDateTimeJalali(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const j = jalaliFromDate(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${j.year}-${String(j.month).padStart(2, '0')}-${String(j.day).padStart(2, '0')}  ${hh}:${mm}`;
}

/** "16:15:00" → "16:15" */
export const formatTime = (time: string): string => time.slice(0, 5);
